// Per-step rule editor — renders all rule types organized in categories.
//
// Common rules (daily loss, drawdown, position size, leverage) are shown
// by default. Advanced rules are behind a toggle per category.

import { For, Show } from 'solid-js';
import type { AuthoredScalingRung, Authority, StepBuilderInput } from '../form';
import type { ScalingCondition } from '../wire';
import RuleToggleEditor from './RuleToggleEditor';
import {
  RULE_CATEGORIES,
  RULE_REGISTRY,
  NEWS_ARMS,
  consequencesForTiming,
  needsCalendarSource,
  commonRulesForCategory,
  advancedRulesForCategory,
  scopedRuleState,
  type RuleBuilderState,
  type RuleCategory,
  type RuleTypeConfig,
} from '../core/ruleRegistry';

import RuleParams from './ruleParams/RuleParams';
import { useMoneyMark, useOrgWords, useTemplatePayoutWords } from './builderWordsContext';

interface StepRulesEditorProps {
  authority: Authority;
  step: StepBuilderInput;
  onUpdateStep: (partial: Partial<StepBuilderInput>) => void;
  rules: RuleBuilderState[];
  onUpdateRules: (rules: RuleBuilderState[]) => void;
  /** The author's search over the rule names. Blank lists every family. */
  query?: string;
  /** Which advanced sections are open — lifted to parent to survive re-renders. */
  advancedOpen: Record<string, boolean>;
  onToggleAdvanced: (key: RuleCategory) => void;
}

export default function StepRulesEditor(props: StepRulesEditorProps) {
  const words = useOrgWords();
  const payout = useTemplatePayoutWords();
  const mark = useMoneyMark();

  function updateRules(rules: RuleBuilderState[]) {
    const before = props.rules.filter((rule) => rule.enabled);
    const after = rules.filter((rule) => rule.enabled);
    const ladder = props.step.scalingLadder;
    if (ladder) props.onUpdateStep({ scalingLadder: { ...ladder, rungs: ladder.rungs.map((rung) => ({
      ...rung, limits: rung.limits.map((limit) => ({ ...limit,
        rule_index: after.findIndex((rule) => rule.id === before[limit.rule_index]?.id),
      })),
    })) } });
    props.onUpdateRules(rules);
  }
  type Rung = AuthoredScalingRung;
  const emptyRung = (): Rung => ({ account_size: '', all_of: [], limits: [], trader_split_pct: null });
  function updateRung(index: number, partial: Partial<Rung>) {
    const ladder = props.step.scalingLadder;
    if (ladder) props.onUpdateStep({ scalingLadder: { ...ladder,
      rungs: ladder.rungs.map((rung, i) => i === index ? { ...rung, ...partial } : rung),
    } });
  }
  function condition(index: number, field: 'net_profit_pct' | 'elapsed_calendar_months', value?: ScalingCondition) {
    const rung = props.step.scalingLadder?.rungs[index];
    if (rung) updateRung(index, { all_of: [...rung.all_of.filter((item) => !(field in item)), ...(value ? [value] : [])] });
  }

  // Rows are keyed by ID, not by rule type: a scopable type may carry several,
  // one per bucket, and two rows of the same type differing only in bucket are
  // two different limits rather than an edit of one.
  function rowsFor(ruleType: string): RuleBuilderState[] {
    return props.rules.filter((r) => r.ruleType === ruleType);
  }

  /** The ACCOUNT row every type has — the one the toggle list renders. */
  function baseRule(ruleType: string): RuleBuilderState | undefined {
    return rowsFor(ruleType)[0];
  }

  function updateRule(id: string, partial: Partial<RuleBuilderState>) {
    updateRules(props.rules.map((r) => (r.id === id ? { ...r, ...partial } : r)));
  }

  function toggleRule(id: string, enabled: boolean) {
    updateRule(id, { enabled });
  }

  function addScopedRule(config: RuleTypeConfig) {
    const base = baseRule(config.key);
    updateRules([
      ...props.rules,
      {
        ...scopedRuleState(config, { kind: 'symbols', value: [] }),
        action: base?.action ?? config.defaultAction,
        valueMode: base?.valueMode ?? (config.defaultValueMode ?? ''),
      },
    ]);
  }

  function removeRule(id: string) {
    updateRules(props.rules.filter((r) => r.id !== id));
  }

  function updateRuleParams(id: string, params: Record<string, unknown>) {
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) clean[k] = v;
    }
    const rule = props.rules.find((r) => r.id === id);
    const arm = NEWS_ARMS.find((choice) => choice.value === clean.arm);
    if (rule?.ruleType === 'news_trading' && clean.arm !== rule.params.arm && arm) {
      const timing = arm.timing;
      const choices = consequencesForTiming(timing, rule.ruleType);
      const compatible = (action: string) =>
        choices.some((choice) => choice.value === action) ? action : '';
      updateRule(id, {
        params: clean,
        timing,
        action: compatible(rule.action),
        repeats: rule.repeats.map(([occurrence, action]) => [occurrence, compatible(action)]),
      });
    } else {
      updateRule(id, { params: clean });
    }
  }

  function enabledCount(rules: RuleTypeConfig[]): number {
    return rules.filter((cfg) => rowsFor(cfg.key).some((r) => r.enabled)).length;
  }

  /** The families whose NAME the author is looking for. */
  function matching(rules: RuleTypeConfig[]): RuleTypeConfig[] {
    const query = (props.query ?? '').trim().toLowerCase();
    return query === ''
      ? rules
      : rules.filter((config) => `${config.label} ${config.description}`.toLowerCase().includes(query));
  }

  function renderRuleList(rules: RuleTypeConfig[]) {
    return (
      <For each={rules}>
        {(config) => (
          <>
            <For each={props.rules.filter((r) => r.ruleType === config.key)}>
              {(rule, index) => (
                <RuleToggleEditor
                  authority={props.authority}
                  rule={rule}
                  config={config}
                  onUpdate={(partial) => updateRule(rule.id, partial)}
                  onToggle={(enabled) => toggleRule(rule.id, enabled)}
                  onRemove={index() > 0 ? () => removeRule(rule.id) : undefined}
                >
                  {config.hasParams
                    ? <RuleParams rule={rule} onUpdateParams={(params) => updateRuleParams(rule.id, params)} />
                    : undefined}
                </RuleToggleEditor>
              )}
            </For>
            <Show when={config.scopable && baseRule(config.key)?.enabled}>
              <button type="button" class="ch-rule-scope-add" onClick={() => addScopedRule(config)}>
                Add a scoped {config.label.toLowerCase()}
              </button>
            </Show>
          </>
        )}
      </For>
    );
  }

  // A search is over the RULE NAMES: while one is typed the sections that are
  // not a rule step aside rather than sitting under a list that excluded them.
  const unfiltered = () => (props.query ?? '').trim() === '';

  return (
    <div class="ch-step-rules">
      <Show when={unfiltered()}>
      <For each={props.rules.filter((rule) => rule.enabled && RULE_REGISTRY[rule.ruleType]?.coreInline)}>
        {(rule) => <RuleToggleEditor authority={props.authority} rule={rule} config={RULE_REGISTRY[rule.ruleType]}
          onUpdate={(partial) => updateRule(rule.id, partial)} onToggle={(enabled) => toggleRule(rule.id, enabled)} />}
      </For>
      <Show when={props.rules.some(needsCalendarSource)}>
        <label class="ch-field">Calendar source policy
          <input class="ch-input" value={props.step.calendarSource ?? ''} placeholder="Exact operator calendar source"
            onInput={(event) => props.onUpdateStep({ calendarSource: event.currentTarget.value })} />
        </label>
      </Show>
      <fieldset class="ch-rule-category">
        <legend>Funded scaling ladder</legend>
        <p>Explicit increasing capital levels. All conditions apply since the last scale; profit surplus is consumed.</p>
        <Show when={props.step.scalingLadder} fallback={<button type="button" onClick={() => props.onUpdateStep({ scalingLadder: { requires_request: true, rungs: [emptyRung(), emptyRung()] } })}>Add ladder</button>}>
          {(ladder) => <>
            <label><input type="checkbox" checked={ladder().requires_request} onChange={(e) => props.onUpdateStep({ scalingLadder: { ...ladder(), requires_request: e.currentTarget.checked } })} />Require trader request</label>
            <For each={ladder().rungs}>{(rung, index) => <fieldset>
              <legend>Level {index()}</legend>
              <label>Allocated capital ({mark})<input class="ch-input" value={rung.account_size} onInput={(e) => updateRung(index(), { account_size: e.currentTarget.value })} /></label>
              <label>Trader split % (blank inherits)<input class="ch-input" value={rung.trader_split_pct ?? ''} onInput={(e) => updateRung(index(), { trader_split_pct: e.currentTarget.value || null })} /></label>
              <Show when={index() > 0}>
                <label>Net profit %<input class="ch-input" value={rung.all_of.find((c) => 'net_profit_pct' in c)?.net_profit_pct.pct ?? ''} onInput={(e) => condition(index(), 'net_profit_pct', e.currentTarget.value ? { net_profit_pct: { pct: e.currentTarget.value, basis: rung.all_of.find((c) => 'net_profit_pct' in c)?.net_profit_pct.basis ?? 'original_initial' } } : undefined)} /></label>
                <label>Profit basis<select class="ch-input" value={rung.all_of.find((c) => 'net_profit_pct' in c)?.net_profit_pct.basis ?? 'original_initial'} onChange={(e) => {
                  const profit = rung.all_of.find((c) => 'net_profit_pct' in c);
                  if (profit) condition(index(), 'net_profit_pct', { net_profit_pct: { ...profit.net_profit_pct, basis: e.currentTarget.value as 'original_initial' | 'current_rung_size' } });
                }}><option value="original_initial">Original initial capital</option><option value="current_rung_size">Current level capital</option></select></label>
                <label>Elapsed calendar months<input class="ch-input" type="number" min="1" step="1" value={rung.all_of.find((c) => 'elapsed_calendar_months' in c)?.elapsed_calendar_months ?? ''} onInput={(e) => condition(index(), 'elapsed_calendar_months', e.currentTarget.value ? { elapsed_calendar_months: Number(e.currentTarget.value) } : undefined)} /></label>
                <For each={rung.limits}>{(limit, limitIndex) => <div>
                  <select class="ch-input" value={limit.rule_index} onChange={(e) => updateRung(index(), { limits: rung.limits.map((item, i) => i === limitIndex() ? { ...item, rule_index: Number(e.currentTarget.value) } : item) })}>
                    <option value="-1">Choose enabled rule</option><For each={props.rules.filter((rule) => rule.enabled)}>{(rule, ruleIndex) => <option value={ruleIndex()}>{rule.ruleType} ({rule.scope.kind})</option>}</For>
                  </select>
                  <label>Native money / lot limit<input class="ch-input" value={limit.value} onInput={(e) => updateRung(index(), { limits: rung.limits.map((item, i) => i === limitIndex() ? { ...item, value: e.currentTarget.value } : item) })} /></label>
                  <button type="button" onClick={() => updateRung(index(), { limits: rung.limits.filter((_, i) => i !== limitIndex()) })}>Remove limit</button>
                </div>}</For>
                <button type="button" onClick={() => updateRung(index(), { limits: [...rung.limits, { rule_index: -1, value: '' }] })}>Add limit</button>
              </Show>
            </fieldset>}</For>
            <button type="button" disabled={ladder().rungs.length >= 32} onClick={() => props.onUpdateStep({ scalingLadder: { ...ladder(), rungs: [...ladder().rungs, emptyRung()] } })}>Add level</button>
            <button type="button" onClick={() => props.onUpdateStep({ scalingLadder: null })}>Remove ladder</button>
          </>}
        </Show>
      </fieldset>
      <fieldset class="ch-rule-category">
        <legend>Consistency and inactivity</legend>
        <Show when={props.step.consistencyMode}>
          <label class="ch-field">Consistency measured against
            <select class="ch-input" aria-label="Consistency measured against" value={props.step.consistencyBasis}
              onChange={(e) => props.onUpdateStep({ consistencyBasis: e.currentTarget.value as StepBuilderInput['consistencyBasis'] })}>
              <option value="total_net_profit">Total net profit</option>
              <option value="positive_days_profit">Profit from positive days only</option>
              <option value="profit_target">Authored profit target</option>
              <option value="gross_incl_payouts">Net profit plus {payout.payouts} already debited</option>
            </select>
          </label>
          <label class="ch-field">When the consistency limit is exceeded
            <select class="ch-input" value={props.step.consistencyRemedy} onChange={(e) => props.onUpdateStep({ consistencyRemedy: e.currentTarget.value as StepBuilderInput['consistencyRemedy'] })}>
              <option value="fail_step">Step cannot pass</option>
              <option value="raise_target">Keep trading; raise target</option>
            </select>
          </label>
          <label class="ch-field">Count profits over
            <select class="ch-input" value={props.step.consistencyWindow} onChange={(e) => props.onUpdateStep({ consistencyWindow: e.currentTarget.value as StepBuilderInput['consistencyWindow'] })}>
              <option value="account_life">Account life</option>
              <option value="since_last_payout">Since the last {payout.payout} debit</option>
            </select>
          </label>
        </Show>
        <label class="ch-field">Days without a fill (blank for no limit)
          <input class="ch-input" aria-label="Days without a fill" type="number" min="1" step="1" value={props.step.inactivityDays}
            onInput={(e) => props.onUpdateStep({ inactivityDays: e.currentTarget.value })} />
        </label>
        <Show when={props.step.inactivityDays}>
          <label class="ch-field">Start inactivity clock at
            <select class="ch-input" value={props.step.inactivityClockFrom} onChange={(e) => props.onUpdateStep({ inactivityClockFrom: e.currentTarget.value as StepBuilderInput['inactivityClockFrom'] })}>
              <option value="purchase">{words.acquired_label}</option><option value="first_trade">First fill</option>
            </select>
          </label>
          <label class="ch-field">When inactive
            <select class="ch-input" value={props.step.inactivityOutcome} onChange={(e) => props.onUpdateStep({ inactivityOutcome: e.currentTarget.value as StepBuilderInput['inactivityOutcome'] })}>
              <option value="expire">Expire account</option><option value="breach">Fail account for inactivity</option>
            </select>
          </label>
          <p>Any fill resets the clock. Days count from the day boundary; the limit is inclusive.</p>
        </Show>
      </fieldset>
      </Show>
      <For each={RULE_CATEGORIES}>
        {(category) => {
          // A search names the rule the author wants, so it opens the advanced
          // list too: a name typed in full must never come back empty.
          const searching = () => (props.query ?? '').trim() !== '';
          const common = () => matching(commonRulesForCategory(category.key));
          const advanced = () => matching(advancedRulesForCategory(category.key));
          const advOpen = () => searching() || !!props.advancedOpen[category.key];
          const advEnabledCount = () => enabledCount(advancedRulesForCategory(category.key));

          return (
            <Show when={common().length + advanced().length > 0}>
              <div class="ch-rule-category">
                <div class="ch-rule-category-header">
                  <span class="ch-rule-category-title">{category.label}</span>
                  <span class="ch-rule-category-desc">{category.description}</span>
                </div>

                {/* Common rules — always visible */}
                <div class="ch-rule-category-body">
                  {renderRuleList(common())}
                </div>

                {/* Advanced rules — behind toggle */}
                <Show when={advanced().length > 0}>
                  <Show when={!searching()}>
                    <button
                      type="button"
                      class="ch-rule-advanced-toggle"
                      onClick={() => props.onToggleAdvanced(category.key)}
                    >
                      <span>
                        {advOpen() ? 'Hide' : 'Show'} advanced rules
                      </span>
                      <Show when={advEnabledCount() > 0 && !advOpen()}>
                        <span class="ch-rule-category-badge">{advEnabledCount()} active</span>
                      </Show>
                      <svg
                        class={`ch-chevron${advOpen() ? '' : ' collapsed'}`}
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                  </Show>

                  <Show when={advOpen()}>
                    <div class="ch-rule-category-body">
                      {renderRuleList(advanced())}
                    </div>
                  </Show>
                </Show>
              </div>
            </Show>
          );
        }}
      </For>
    </div>
  );
}
